import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, forkJoin, map, Observable, of, Subject, switchMap } from 'rxjs';
import { Role } from '@/core/models/auth.interface';
import { AuthService } from '@/core/services/auth.service';
import { PaginatedResponse } from '@/shared/models/paginated-response.model';
import { HelpersService } from '@/shared/services/helpers.service';
import { QuestionsApiService } from './data-access/questions-api.service';
import { GeneralQuestionCategory, QuestionRole, QuestionScope, QuestionTab } from './data-access/questions.enum';
import { mapDealQuestionToListItem, mapGeneralQuestionToListItem } from './data-access/questions.helper';
import {
    AvailableDealDto,
    EscalateQuestionPayload,
    PostQuestionAnswerPayload,
    QuestionListItem,
    QuestionRef,
    QuestionsApiEnvelope,
    QuestionsListFilters,
    QuestionsListQuery,
    QuestionsListRequest,
    RaiseDealQuestionPayload,
    RaiseGeneralQuestionPayload
} from './data-access/questions.model';

const EMPTY_TAB_COUNTS: Record<QuestionTab, number> = {
    [QuestionTab.DEALS]: 0,
    [QuestionTab.ONBOARDING]: 0,
    [QuestionTab.OTHER]: 0
};

interface QuestionsPage {
    items: QuestionListItem[];
    total: number;
}

const EMPTY_PAGE: QuestionsPage = { items: [], total: 0 };

interface ListRequest {
    request: QuestionsListRequest;
    onLoaded?: () => void;
}

@Injectable({
    providedIn: 'root'
})
export class QuestionsService {
    /** The page currently on screen — the API decides its contents, not the client. */
    public readonly questions = signal<QuestionListItem[]>([]);
    public readonly totalRecords = signal<number>(0);

    /** Server-side totals behind each tab label, refreshed whenever the filters change. */
    public readonly tabCounts = signal<Record<QuestionTab, number>>(EMPTY_TAB_COUNTS);

    private readonly _listLoading = signal<boolean>(false);
    public readonly listLoading = this._listLoading.asReadonly();

    public readonly submitting = signal<boolean>(false);

    private readonly _acceptingKey = signal<string | null>(null);

    /** Deals selectable when raising a deal question. Populated by `loadDealOptions()`. */
    public readonly dealOptions = signal<Array<{ label: string; value: number }>>([]);

    private readonly _questionsApiService = inject(QuestionsApiService);
    private readonly _helpersService = inject(HelpersService);
    private readonly _translateService = inject(TranslateService);
    private readonly _authService = inject(AuthService);

    private readonly _listRequests$ = new Subject<ListRequest>();
    private readonly _tabCountRequests$ = new Subject<QuestionsListFilters>();

    /**
     * The caller's role bucket, matching the API's `DealQuestionRole` values.
     * Used to decide who may accept an answer: the list endpoints only return the
     * caller's own questions unless they are staff, so a matching role on a row
     * means the current user raised it.
     */
    public readonly currentRoleBucket = computed(() => toQuestionRole(this._authService.getAccessTokenRole()));

    public readonly canRaiseQuestions = computed(() => this.currentRoleBucket() !== QuestionRole.ADMIN);

    constructor() {
        this._listRequests$
            .pipe(
                switchMap(({ request, onLoaded }) => {
                    this._listLoading.set(true);
                    return this.fetchPage(request).pipe(
                        finalize(() => this._listLoading.set(false)),
                        map((page) => ({ page, onLoaded }))
                    );
                }),
                takeUntilDestroyed()
            )
            .subscribe(({ page, onLoaded }) => {
                this.questions.set(page.items);
                this.totalRecords.set(page.total);
                onLoaded?.();
            });

        this._tabCountRequests$
            .pipe(
                switchMap((filters) => this.fetchTabCounts(filters)),
                takeUntilDestroyed()
            )
            .subscribe((counts) => this.tabCounts.set(counts));
    }

    /**
     * Loads the requested page. `onLoaded` runs once `questions()` holds it, and never runs
     * for a request a newer one has superseded — switching tabs or typing mid-request cancels
     * the older call rather than letting its response land last.
     */
    public loadQuestions(request: QuestionsListRequest, onLoaded?: () => void): void {
        this._listRequests$.next({ request, onLoaded });
    }

    /**
     * The counts on the tab labels. Each is a `total` off a one-row page, so the numbers come
     * from the API rather than from whatever happens to be on screen. Only the filters move
     * them — paging and switching tabs do not, so callers refresh them on filter changes.
     */
    public loadTabCounts(filters: QuestionsListFilters): void {
        this._tabCountRequests$.next(filters);
    }

    private fetchTabCounts(filters: QuestionsListFilters): Observable<Record<QuestionTab, number>> {
        const query: QuestionsListQuery = { ...filters, page: 1, pageSize: 1 };

        return forkJoin({
            deals: this.countOf(this._questionsApiService.getDealQuestions(query)),
            onboarding: this.countOf(this._questionsApiService.getGeneralQuestions(query, GeneralQuestionCategory.COMPLIANCE_ONBOARDING)),
            other: this.countOf(this._questionsApiService.getGeneralQuestions(query, GeneralQuestionCategory.OTHER))
        }).pipe(
            map(({ deals, onboarding, other }) => ({
                [QuestionTab.DEALS]: deals,
                [QuestionTab.ONBOARDING]: onboarding,
                [QuestionTab.OTHER]: other
            }))
        );
    }

    /**
     * Each tab is one resource — Deals the deal questions, the other two a category of the
     * general ones — so a page is always exactly one request, sorted and counted by the API.
     * A failed request leaves an empty page behind; `errorInterceptor` has already reported it.
     */
    private fetchPage(request: QuestionsListRequest): Observable<QuestionsPage> {
        const query: QuestionsListQuery = { status: request.status, createdByRole: request.createdByRole, page: request.page, pageSize: request.pageSize };

        if (request.tab === QuestionTab.DEALS) {
            return this._questionsApiService.getDealQuestions(query).pipe(
                map((response) => ({ items: response.items.map(mapDealQuestionToListItem), total: response.total })),
                catchError(() => of(EMPTY_PAGE))
            );
        }

        return this._questionsApiService.getGeneralQuestions(query, categoryForTab(request.tab)).pipe(
            map((response) => ({ items: response.items.map(mapGeneralQuestionToListItem), total: response.total })),
            catchError(() => of(EMPTY_PAGE))
        );
    }

    /**
     * For callers holding a reference instead of a list row — a task. Mapped to the list's own
     * shape so the thread drawer renders either source identically. `null` means gone or not
     * visible to the caller; `errorInterceptor` has already reported the failure.
     */
    public getQuestionById(ref: QuestionRef): Observable<QuestionListItem | null> {
        const request$ =
            ref.scope === QuestionScope.DEAL
                ? this._questionsApiService.getDealQuestionById(ref.id).pipe(map((dto) => (dto?.id != null ? mapDealQuestionToListItem(dto) : null)))
                : this._questionsApiService.getGeneralQuestionById(ref.id).pipe(map((dto) => (dto?.id != null ? mapGeneralQuestionToListItem(dto) : null)));

        return request$.pipe(catchError(() => of(null)));
    }

    private countOf(request$: Observable<PaginatedResponse<unknown>>): Observable<number> {
        return request$.pipe(
            map((response) => response.total),
            catchError(() => of(0))
        );
    }

    public raiseGeneralQuestion(payload: RaiseGeneralQuestionPayload, onSuccess?: () => void): void {
        this.submitting.set(true);
        this._questionsApiService
            .raiseGeneralQuestion(payload)
            .pipe(finalize(() => this.submitting.set(false)))
            .subscribe((response) => {
                if (!this.isEnvelopeSuccessful(response)) return;
                this._helpersService.showSuccessMessage(this._translateService.instant('QUESTIONS.ASK.SUCCESS'));
                onSuccess?.();
            });
    }

    /** The API decides which deals the caller may ask about, so there is no role branching here. */
    public loadDealOptions(): void {
        this._questionsApiService
            .getAvailableDeals()
            .pipe(catchError(() => of([] as AvailableDealDto[])))
            .subscribe((deals) => {
                this.dealOptions.set(deals.map(toDealOption).filter((option) => option != null));
            });
    }

    public raiseDealQuestion(dealId: number, payload: RaiseDealQuestionPayload, onSuccess?: () => void): void {
        this.submitting.set(true);
        this._questionsApiService
            .raiseDealQuestion(dealId, payload)
            .pipe(finalize(() => this.submitting.set(false)))
            .subscribe((response) => {
                if (!this.isEnvelopeSuccessful(response)) return;
                this._helpersService.showSuccessMessage(this._translateService.instant('QUESTIONS.ASK.SUCCESS'));
                onSuccess?.();
            });
    }

    public postReply(question: QuestionListItem, payload: PostQuestionAnswerPayload, onSuccess?: () => void): void {
        const request$ = question.scope === QuestionScope.DEAL ? this._questionsApiService.answerDealQuestion(question.id, payload) : this._questionsApiService.answerGeneralQuestion(question.id, payload);

        this.submitting.set(true);
        request$.pipe(finalize(() => this.submitting.set(false))).subscribe((response) => {
            if (!this.isEnvelopeSuccessful(response)) return;
            this._helpersService.showSuccessMessage(this._translateService.instant('QUESTIONS.THREAD.REPLY_SUCCESS'));
            onSuccess?.();
        });
    }

    public escalate(question: QuestionListItem, payload: EscalateQuestionPayload, onSuccess?: () => void): void {
        if (question.scope === QuestionScope.DEAL && question.dealOpportunityId == null) {
            this._helpersService.showErrorMessage(this._translateService.instant('QUESTIONS.THREAD.ESCALATE_MISSING_DEAL'));
            return;
        }

        const request$: Observable<unknown> =
            question.scope === QuestionScope.DEAL ? this._questionsApiService.escalateDealQuestion(question.dealOpportunityId as number, question.id, payload) : this._questionsApiService.escalateGeneralQuestion(question.id, payload);

        this.submitting.set(true);
        request$.pipe(finalize(() => this.submitting.set(false))).subscribe((response) => {
            if (!this.isEnvelopeSuccessful(response)) return;
            this._helpersService.showSuccessMessage(this._translateService.instant('QUESTIONS.THREAD.ESCALATE_SUCCESS'));
            onSuccess?.();
        });
    }

    public acceptAnswer(question: QuestionListItem, onSuccess?: () => void): void {
        const request$: Observable<unknown> = question.scope === QuestionScope.DEAL ? this._questionsApiService.acceptDealQuestionAnswer(question.id) : this._questionsApiService.acceptGeneralQuestionAnswer(question.id);

        this._acceptingKey.set(questionKey(question));
        this.submitting.set(true);
        request$
            .pipe(
                finalize(() => {
                    this.submitting.set(false);
                    this._acceptingKey.set(null);
                })
            )
            .subscribe((response) => {
                if (!this.isEnvelopeSuccessful(response)) return;
                this._helpersService.showSuccessMessage(this._translateService.instant('QUESTIONS.THREAD.ACCEPT_SUCCESS'));
                onSuccess?.();
            });
    }

    public isAcceptingAnswer(question: QuestionListItem): boolean {
        return this._acceptingKey() === questionKey(question);
    }

    public canAcceptAnswer(question: QuestionListItem): boolean {
        return question.canAcceptAnswer;
    }

    /**
     * Same rule the rest of the app uses (see new-tasks / notifications components):
     * `envelopeInterceptor` strips the envelope on success, so only an explicit
     * `success: false` / `isFailure` is a business failure. Transport errors have already
     * been reported by `errorInterceptor`.
     */
    private isEnvelopeSuccessful(response: unknown): boolean {
        const res = response as Partial<QuestionsApiEnvelope<unknown>> | null;
        if (!(res?.success === false || res?.isFailure)) return true;

        this._helpersService.showErrorMessage(res.errors?.[0] ?? this._translateService.instant('GENERIC.ERROR'));
        return false;
    }
}

function questionKey(question: QuestionListItem): string {
    return `${question.scope}:${question.id}`;
}

/** The two topic tabs are the two general-question categories. */
function categoryForTab(tab: QuestionTab): GeneralQuestionCategory {
    return tab === QuestionTab.ONBOARDING ? GeneralQuestionCategory.COMPLIANCE_ONBOARDING : GeneralQuestionCategory.OTHER;
}

function toDealOption(deal: AvailableDealDto): { label: string; value: number } | null {
    const id = deal.id ?? deal.dealOpportunityId;
    if (id == null) return null;

    return { label: (deal.name ?? deal.dealName)?.trim() || `#${id}`, value: id };
}

function toQuestionRole(role: Role): QuestionRole {
    switch (role) {
        case Role.WEALTH_MANAGER_SUPER_USER:
        case Role.WEALTH_MANAGER_DEALS:
        case Role.WEALTH_MANAGER_USERS:
            return QuestionRole.WEALTH_MANAGER;
        case Role.RELATIONSHIP_MANAGER:
            return QuestionRole.RELATIONSHIP_MANAGER;
        case Role.CLIENT:
            return QuestionRole.CLIENT;
        default:
            return QuestionRole.ADMIN;
    }
}
